import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { trpc } from '~/api/trpc';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';

interface TestPgBouncerDialogProps {
    open: boolean;
    onClose: () => void;
}

const TestPgBouncerDialog = ({ open, onClose }: TestPgBouncerDialogProps) => {
    const query = trpc.testing.testDatabaseConnectionPgBouncer.useQuery(undefined, {
        enabled: open,
        retry: false,
    });

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <DialogContent className="sm:max-w-[450px]">
                <DialogHeader>
                    <DialogTitle>PgBouncer Connection Test</DialogTitle>
                    <DialogDescription>
                        Testing database connectivity through PgBouncer.
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4">
                    {query.isLoading && (
                        <div className="flex items-center gap-3 p-4 border rounded-md">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">Connecting to PgBouncer...</p>
                        </div>
                    )}

                    {query.isSuccess && (
                        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-md text-green-800">
                            <CheckCircle className="h-5 w-5 mt-0.5 shrink-0" />
                            <div>
                                <p className="font-medium">Connection Successful</p>
                                <p className="text-sm mt-1">{query.data.message}</p>
                                <pre className="text-xs mt-2 bg-green-100 rounded p-2 overflow-auto">
                                    {JSON.stringify(query.data.result, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}

                    {query.isError && (
                        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
                            <XCircle className="h-5 w-5 mt-0.5 shrink-0" />
                            <div>
                                <p className="font-medium">Connection Failed</p>
                                <p className="text-sm mt-1">{query.error.message}</p>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Close
                    </Button>
                    <Button
                        onClick={() => query.refetch()}
                        disabled={query.isLoading}
                    >
                        {query.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Retry
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default TestPgBouncerDialog;
